/* eslint-disable @typescript-eslint/no-unnecessary-condition */
import { ModelEntityType } from '@upright/common/model/input/types';
import { SdgAlignmentStrength, SdgId, SdgUnitsMode } from '@upright/common/model/output/scores/sdg-alignment/types';
import { getAlignmentsOfValenceWithAdjustedValues } from '@upright/common/model/output/scores/sdg-alignment/utils';
import { usePushGtmEvent } from '@upright/ui/common/generic/components/google-tag-manager/use-push-gtm-event';
import { CustomGtmEvent } from '@upright/ui/common/generic/components/google-tag-manager/use-push-gtm-event/payloads';
import { BouColor } from '@upright/ui/common/generic/global/bou-colors';
import { Side } from '@upright/ui/common/upright/core/impact-lens-profile/common/layout/types';
import { sideToValence } from '@upright/ui/common/upright/core/impact-lens-profile/common/layout/utils';
import { useCarouselLayout } from '@upright/ui/common/upright/core/impact-lens-profile/context';
import { sdgLensLayout } from '@upright/ui/common/upright/core/impact-lenses/sdg/common/layout';
import { useSdgLensContext } from '@upright/ui/common/upright/core/impact-lenses/sdg/common/use-sdg-lens-context';
import { toSdgAligmentValence } from '@upright/ui/common/upright/core/impact-lenses/sdg/common/utils';
import { SdgBoxConnector } from '@upright/ui/common/upright/core/impact-lenses/sdg/data-area/sdg-box-connector';
import { sdgDrilldownModalMinScreenWidth } from '@upright/ui/common/upright/model/output/scores/sdg-alignments/sdg-drilldown-modal/constants';
import { getSdgIconComponent } from '@upright/ui/common/upright/model/output/scores/sdg-alignments/sdg-row/sdg-icons/get-sdg-icon';
import { getSdgColor } from '@upright/ui/common/upright/model/output/scores/sdg-alignments/shared';
import styled from 'styled-components';
import { SdgBarTexts } from './sdg-bar-texts';
import { useSdgUnitsMode } from '@upright/ui/common/upright/model/output/scores/sdg-alignments/use-sdg-unit-mode';

interface Props {
  side: Side;
  sdgId: SdgId;
}

export function SdgBoxWithBar(props: Props) {
  const { side, sdgId } = props;
  const ctx = useSdgLensContext();
  const baseLayout = useCarouselLayout();
  const pushGtmEvent = usePushGtmEvent();
  const [unitsMode] = useSdgUnitsMode();

  if (!ctx) {
    return <></>;
  }
  const data = ctx.data.sdgAlignments;
  if (!data) {
    return <></>;
  }

  const layout = sdgLensLayout(baseLayout, data, unitsMode);
  const boxArea = layout.sdgBoxArea(side, sdgId);
  const strongArea = layout.sdgBoxStrongBarArea(side, sdgId);
  const normalArea = layout.sdgBoxNormalBarArea(side, sdgId);
  const weakArea = layout.sdgBoxWeakBarArea(side, sdgId);
  const textAreas = layout.getSdgTextsAreaBounds(side, sdgId);
  const Icon = getSdgIconComponent(sdgId);
  const narrowMode = layout.narrowMode;

  const valence = toSdgAligmentValence(sideToValence(props.side));
  const alignment = data[sdgId];
  const alignmentsWithAdjustedValues = alignment
    ? getAlignmentsOfValenceWithAdjustedValues(alignment, valence, unitsMode)
    : undefined;

  const shouldShowTexts = !!alignmentsWithAdjustedValues;
  const shouldShowBreakDown = !narrowMode && unitsMode === SdgUnitsMode.PURE_REVENUE_SHARES;

  const strongColor = getSdgColor(valence, SdgAlignmentStrength.STRONG);
  const normalColor = getSdgColor(
    valence,
    shouldShowBreakDown ? SdgAlignmentStrength.NORMAL : SdgAlignmentStrength.STRONG,
  );
  const weakColor = getSdgColor(valence, shouldShowBreakDown ? SdgAlignmentStrength.WEAK : SdgAlignmentStrength.STRONG);

  const hoverSpacing = 3;

  // The same entity rule as `SdgDrillDownBox`, which renders the modal: a goal on a product profile
  // has no breakdown to open, so it must not look clickable.
  const entityType = ctx.data.entity?.type ?? ModelEntityType.COMPANY;
  const hasDrilldown =
    entityType === ModelEntityType.COMPANY ||
    entityType === ModelEntityType.GROUP ||
    entityType === ModelEntityType.PORTFOLIO;
  const canOpenModal = hasDrilldown && baseLayout.fullArea().width >= sdgDrilldownModalMinScreenWidth;

  return (
    <G
      style={{
        cursor: canOpenModal ? 'pointer' : undefined,
      }}
      onClick={() => {
        if (canOpenModal) {
          ctx.setSelectionState({ sdg: sdgId, valence });
          pushGtmEvent({
            event: CustomGtmEvent.IMPACT_PROFILE_SDG_DRILLDOWN_OPEN,
            data: {
              valence,
              sdgId,
            },
          });
        }
      }}
    >
      {canOpenModal && (
        <HoverRect
          rx={2}
          ry={2}
          x={side === Side.RIGHT ? boxArea.x1 - hoverSpacing : textAreas.sum.x1 - hoverSpacing}
          y={boxArea.y1 - hoverSpacing}
          height={boxArea.height + hoverSpacing * 2}
          width={
            side === Side.RIGHT
              ? textAreas.sum.x2 - boxArea.x1 + hoverSpacing * 2
              : boxArea.x2 - textAreas.sum.x1 + hoverSpacing * 2
          }
          fill="transparent"
        />
      )}
      <Icon x={boxArea.x1} y={boxArea.y1} width={boxArea.width} height={boxArea.height} />
      {!narrowMode && (
        <rect
          fill={strongColor}
          x={strongArea.x1}
          y={strongArea.y1}
          width={strongArea.width}
          height={strongArea.height}
        />
      )}
      {!narrowMode && (
        <rect
          fill={normalColor}
          x={normalArea.x1}
          y={normalArea.y1}
          width={normalArea.width}
          height={normalArea.height}
        />
      )}
      {!narrowMode && (
        <rect fill={weakColor} x={weakArea.x1} y={weakArea.y1} width={weakArea.width} height={weakArea.height} />
      )}
      {shouldShowTexts && (
        <SdgBarTexts
          side={side}
          textAreaBounds={textAreas}
          alignments={alignmentsWithAdjustedValues}
          shouldShowBreakDown={shouldShowBreakDown}
        />
      )}
      <SdgBoxConnector sdgId={sdgId} valence={sideToValence(side)} />
    </G>
  );
}

const HoverRect = styled.rect`
  transition: stroke 0.2s ease-in-out;
  stroke: white;
`;

const G = styled.g`
  &:hover ${HoverRect} {
    stroke: ${BouColor.TOOL_BORDER_LIGHT_GRAY};
  }

  &:active ${HoverRect} {
    stroke: #7400b8;
  }
`;
